import { useEffect } from 'react';
import { site } from '@/content/site';
import { services } from '@/content/services';
import { contact } from '@/content/contact';

const ORIGIN = `https://${site.domain}`;

/**
 * Organization + WebSite + per-office LocalBusiness, as JSON-LD.
 *
 * This is where the location signal actually lives for search engines: the
 * page copy names Calgary and Lahore in prose, but `areaServed` and a typed
 * PostalAddress per office is what a crawler can read unambiguously.
 *
 * Injected once at app level rather than per page — the graph describes the
 * company, not the route, so re-emitting it on every navigation would be
 * duplicate markup.
 */
export function buildGraph() {
  const offices = site.locations.map((loc) => ({
    '@type': 'LocalBusiness',
    '@id': `${ORIGIN}/#office-${loc.city.toLowerCase()}`,
    name: `${site.name} — ${loc.city}`,
    parentOrganization: { '@id': `${ORIGIN}/#organization` },
    url: `${ORIGIN}/contact`,
    email: site.email.general,
    address: {
      '@type': 'PostalAddress',
      addressLocality: loc.city,
      addressRegion: loc.region,
      addressCountry: loc.country,
    },
    areaServed: loc.country,
  }));

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': `${ORIGIN}/#organization`,
        name: site.name,
        legalName: site.legalName,
        url: ORIGIN,
        email: site.email.general,
        slogan: site.tagline,
        logo: {
          '@type': 'ImageObject',
          url: `${ORIGIN}/og-image.png`,
        },
        sameAs: Object.values(site.social),
        address: site.locations.map((loc) => ({
          '@type': 'PostalAddress',
          addressLocality: loc.city,
          addressRegion: loc.region,
          addressCountry: loc.country,
        })),
        // Describe the services without publishing unconfirmed prices.
        makesOffer: [
          ...services.map((s) => ({
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: s.name,
              url: `${ORIGIN}/services/${s.slug}`,
              description: s.summary,
            },
          })),

        ],
      },
      {
        '@type': 'WebSite',
        '@id': `${ORIGIN}/#website`,
        url: ORIGIN,
        name: site.name,
        publisher: { '@id': `${ORIGIN}/#organization` },
        inLanguage: 'en',
      },
      /*
        The contact page's FAQ, in the form Google can render as a rich result.
        Sourced from the same content/contact.ts the page renders, so the
        markup can never claim a question the page does not actually show —
        which is both a correctness matter and a structured-data policy one.
      */
      {
        '@type': 'FAQPage',
        '@id': `${ORIGIN}/contact#faq`,
        url: `${ORIGIN}/contact`,
        mainEntity: contact.faq.items.map((item) => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: { '@type': 'Answer', text: item.a },
        })),
      },
      ...offices,
    ],
  };
}

export function StructuredData() {
  useEffect(() => {
    const id = 'falcon-structured-data';
    if (document.getElementById(id)) return;

    const script = document.createElement('script');
    script.id = id;
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(buildGraph());
    document.head.appendChild(script);

    return () => {
      document.getElementById(id)?.remove();
    };
  }, []);

  return null;
}
